import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import '../../styles/Forms.css';

const AddSitePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    isActive: true,
  });

  useEffect(() => {
    if (isEdit) {
      fetchSite();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSite = async () => {
    try {
      setLoading(true);
      const res = await api.getSite(id);
      if (res.success && res.data) {
        const site = res.data;
        setFormData({
          name: site.name || '',
          street: site.address?.street || '',
          city: site.address?.city || '',
          state: site.address?.state || '',
          zipCode: site.address?.zipCode || '',
          country: site.address?.country || '',
          contactName: site.contactPerson?.name || '',
          contactEmail: site.contactPerson?.email || '',
          contactPhone: site.contactPerson?.phone || '',
          isActive: site.isActive !== undefined ? site.isActive : true,
        });
      } else {
        toast.error('Site not found');
        navigate('/admin/sites');
      }
    } catch (error) {
      console.error('Error loading site:', error);
      toast.error('Error loading site details');
      navigate('/admin/sites');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter site name');
      return;
    }

    // Validate contact person
    if (!formData.contactName.trim()) {
      toast.error('Please enter contact person name');
      return;
    }

    if (!formData.contactEmail.trim()) {
      toast.error('Please enter contact person email');
      return;
    }

    if (!formData.contactPhone.trim()) {
      toast.error('Please enter contact person phone');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name.trim(),
        address: {
          street: formData.street.trim() || undefined,
          city: formData.city.trim() || undefined,
          state: formData.state.trim() || undefined,
          zipCode: formData.zipCode.trim() || undefined,
          country: formData.country.trim() || undefined,
        },
        contactPerson: {
          name: formData.contactName.trim(),
          email: formData.contactEmail.trim(),
          phone: formData.contactPhone.trim(),
        },
        isActive: formData.isActive,
      };

      let res;
      if (isEdit) {
        res = await api.updateSite(id, payload);
      } else {
        res = await api.createSite(payload);
      }

      if (res.success) {
        toast.success(isEdit ? 'Site updated successfully!' : 'Site created successfully!');
        navigate(-1);
      } else {
        toast.error(
          (isEdit ? 'Error updating site: ' : 'Error creating site: ') + (res.message || 'Unknown error')
        );
      }
    } catch (error) {
      console.error(isEdit ? 'Error updating site:' : 'Error creating site:', error);
      toast.error(
        (isEdit ? 'Error updating site: ' : 'Error creating site: ') + (error.message || 'Unknown error')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-site-page" style={{ padding: '16px' }}>
      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.3fr 1.7fr',
            gap: '20px',
            marginBottom: '20px',
          }}
        >
          {/* Left column - basic info */}
          <div
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <h2
              style={{
                margin: '0 0 16px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: '#2d3748',
              }}
            >
              Site Details
            </h2>

            <div className="form-group">
              <label htmlFor="name">Site Name *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter site name"
                required
              />
            </div>

            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
              <input
                id="isActive"
                name="isActive"
                type="checkbox"
                checked={formData.isActive}
                onChange={handleInputChange}
                style={{ marginRight: '6px' }}
              />
              <label htmlFor="isActive" style={{ margin: 0, fontSize: '13px', color: '#4a5568' }}>
                Active
              </label>
            </div>
          </div>

          {/* Right column - address & contact */}
          <div>
            <div
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '16px',
              }}
            >
              <h2
                style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2d3748',
                }}
              >
                Address
              </h2>

              <div className="form-group">
                <label htmlFor="street">Street</label>
                <input
                  id="street"
                  name="street"
                  type="text"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="Street address"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    id="state"
                    name="state"
                    type="text"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="State"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div className="form-group">
                  <label htmlFor="zipCode">Zip / Postal Code</label>
                  <input
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="e.g., 400001"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <input
                    id="country"
                    name="country"
                    type="text"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <h2
                style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2d3748',
                }}
              >
                Contact Person
              </h2>

              <div className="form-group">
                <label htmlFor="contactName">Name *</label>
                <input
                  id="contactName"
                  name="contactName"
                  type="text"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  placeholder="Contact person name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactEmail">Email *</label>
                <input
                  id="contactEmail"
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  placeholder="Contact email"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactPhone">Phone *</label>
                <input
                  id="contactPhone"
                  name="contactPhone"
                  type="text"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  placeholder="Contact phone"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form actions */}
        <div
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
            paddingTop: '16px',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              padding: '10px 20px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update Site' : 'Create Site'}
          </button>
        </div>
      </form>

      {loading && <Loader />}
    </div>
  );
};

export default AddSitePage;

